import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { useQueryCompanyBillingSummary } from '../api/useQueryBillingStats'
import type { BillingPeriod, BillingDriverGroup } from '../types'
import BillingSummaryDriversModal from './BillingSummaryDriversModal'

interface BillingSummaryFooterProps {
  companyId: string
  period: BillingPeriod
}

const fmt = (n: number | null | undefined): string =>
  n == null ? '-' : new Intl.NumberFormat().format(n)

const KpiInline: React.FC<{ label: string; value: React.ReactNode; tooltip: string; onClick?: () => void }> = ({
  label,
  value,
  tooltip,
  onClick,
}) => (
  <Tooltip text={tooltip} asChild portal maxWidth="22rem">
    <button type="button" onClick={onClick} disabled={!onClick} className="tw-mb-0 tw-whitespace-nowrap tw-rounded tw-text-left tw-text-sm hover:tw-underline focus-visible:tw-outline focus-visible:tw-outline-primary disabled:tw-cursor-default">
      <span className="tw-text-content-secondary">{label}:</span>{' '}
      <span className="tw-font-medium">{value}</span>
    </button>
  </Tooltip>
)

const Divider: React.FC = () => (
  <div className="tw-h-5 tw-w-0.5 tw-shrink-0 tw-bg-secondary" />
)

const BillingSummaryFooter: React.FC<BillingSummaryFooterProps> = ({
  companyId,
  period,
}) => {
  const { t } = useLocalize()
  const { data: summary } = useQueryCompanyBillingSummary(companyId, period)
  const [selected, setSelected] = React.useState<{ group: BillingDriverGroup; companyId: string; period: string } | null>(null)
  React.useEffect(() => { setSelected(null) }, [companyId, period])
  const labels: Record<BillingDriverGroup, [string, string]> = {
    active: ['activeDrivers', 'activeDrivers'],
    contract: ['contractDrivers', 'contractDrivers'],
    timesheet: ['timesheetDrivers', 'timesheetDrivers'],
    calculations: ['calculationsPerformed', 'calculationsPerformed'],
    withoutActivity: ['driversWithoutActivity', 'driversWithoutActivity'],
    zeroPayout: ['driversWithZeroPayout', 'driversWithZeroPayout'],
    onlyEquivalent: ['onlyLeaveEquivalent', 'onlyLeaveEquivalent'],
  }
  const openGroup = (group: BillingDriverGroup) => summary?.driverGroups?.[group]
    ? () => setSelected({ group, companyId, period }) : undefined
  const currentGroup = selected?.companyId === companyId && selected?.period === period ? selected.group : null

  return (
    <div className="tw-flex tw-min-h-10 tw-flex-wrap tw-shrink-0 tw-items-center tw-gap-3 tw-border-t tw-border-base-300 tw-bg-content-primary tw-px-4 tw-py-2">
      <KpiInline
        label={t('billing.col.activeDrivers')}
        value={fmt(summary?.activeDriversCount)}
        tooltip={t('billing.tip.activeDrivers')}
        onClick={openGroup('active')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.contractDrivers')} ${period}`}
        value={fmt(summary?.driversWithContractCount)}
        tooltip={t('billing.tip.contractDrivers')}
        onClick={openGroup('contract')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.timesheetDrivers')} ${period}`}
        value={fmt(summary?.driversWithTimesheetCalculated)}
        tooltip={t('billing.tip.timesheetDrivers')}
        onClick={openGroup('timesheet')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.calculationsPerformed')} ${period}`}
        value={`${fmt(summary?.timesheetCalculationsCount)} (${fmt(summary?.driversCalculatedInMonth)} ${t('billing.driversCountUnit')})`}
        tooltip={t('billing.tip.calculationsPerformed')}
        onClick={openGroup('calculations')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.driversWithoutActivity')} ${period}`}
        value={fmt(summary?.driversWithoutActivityCount)}
        tooltip={t('billing.tip.driversWithoutActivity')}
        onClick={openGroup('withoutActivity')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.driversWithZeroPayout')} ${period}`}
        value={fmt(summary?.driversWithZeroPayoutCount)}
        tooltip={t('billing.tip.driversWithZeroPayout')}
        onClick={openGroup('zeroPayout')}
      />
      <Divider />
      <KpiInline
        label={`${t('billing.col.onlyLeaveEquivalent')} ${period}`}
        value={fmt(summary?.driversWithOnlyLeaveEquivalentCount)}
        tooltip={t('billing.tip.onlyLeaveEquivalent')}
        onClick={openGroup('onlyEquivalent')}
      />
      {currentGroup && summary?.driverGroups?.[currentGroup] && (
        <BillingSummaryDriversModal
          key={`${companyId}:${period}:${currentGroup}`}
          title={`${t(`billing.col.${labels[currentGroup][0]}`)} ${period}`}
          description={t(`billing.tip.${labels[currentGroup][1]}`)}
          drivers={summary.driverGroups[currentGroup]}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

export default BillingSummaryFooter
